import { IController } from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';

export class StudentNotesPanelCtrl implements IController {
  static $inject = [
    'Note',
    'NoteModal',
    '$scope',
    'UtilitiesService',
    'ErrorModal',
    'NoteConfirmDeletionModal',
    'ImNote',
    'ImUser',
  ];

  // bindings
  private student;
  private currentUser;

  // local
  private moment;
  private direction;
  private studentName;
  private sortByOptions;
  private filteredNotes;
  private categoryFilter;
  private notes;
  private supportFilter;
  private notesThatCanBeEditedHash;
  private notesThatCanBeDeletedHash;
  private notesLastEditDate;
  private _standardCategoryOptions;
  private supportOptions;
  private categoryOptionsToShow;

  constructor(
    private Note,
    private NoteModal,
    private $scope,
    private UtilitiesService,
    private ErrorModal,
    private NoteConfirmDeletionModal,
    private ImNote,
    private imUser: ImUser,
  ) {}

  $onInit() {
    this.moment = moment;
    this.direction = true;
    const { first, last } = this.student.studentDetails.name;
    this.studentName = `${first} ${last}`;

    this.sortByOptions = [
      ['Date created', 'dateCreated'],
      ['Created by', 'createdBy.lastName'],
      ['Category', 'category'],
      ['Support', 'attachments.support.name'],
    ];

    this.notesThatCanBeEditedHash = {};
    this.notesThatCanBeDeletedHash = {};
    this.notesLastEditDate = {};

    // keep in sync with backend values for notes.category
    this._standardCategoryOptions = [
      'Attendance',
      'Credits',
      'Diploma Notes',
      'Other',
      'Post-Secondary Notes',
      'Regents',
      'Support Specific',
    ];

    // create copy of self._standardCategoryOptions
    this.categoryOptionsToShow = this._standardCategoryOptions.slice(0);
    // TODO: this API request should be moved to the resolve block (CM)
    let promise = this.Note.query({
      studentId: this.student._id,
    }).$promise;

    promise.then(
      notes => {
        this._setNotesAfterInitialFetch(notes);
        this.supportOptions = _.map(notes, function(note: any) {
          return this.ImNote.supportName(note);
        });
        this.supportOptions = _.compact(_.uniq(this.supportOptions));
      },
      function() {
        // TODO: Handle failure case?
      },
    );

    this.$scope.$on('newNote', (event, note) => {
      const noteId = note._id;
      const { canView, canEdit, canDelete, humanDateCreated, categoryOtherVal } = this._getPanelPropsForNote(note);

      // add note to notes and refilter
      // tslint:disable-next-line:no-unused-expression
      if (canView) this.notes.push(note) && this.filterNotes();

      // update hashes
      // tslint:disable-next-line:max-line-length
      this.notesLastEditDate[noteId] =
        this.UtilitiesService.convertTimestampToLocalTime(note.lastEditedAt) || humanDateCreated;
      if (canEdit) this.notesThatCanBeEditedHash[noteId] = true;
      if (canDelete) this.notesThatCanBeDeletedHash[noteId] = true;

      // add menu option to category options filter menu
      if (categoryOtherVal) {
        const newCategoryOpts = _.uniq(this.categoryOptionsToShow.concat([categoryOtherVal]));
        const sortedNewCategoryOpts = this._sortStringArrCaseInsensitive(newCategoryOpts);

        this.categoryOptionsToShow = sortedNewCategoryOpts;
      }
    });
  }

  // methods

  changeDirection() {
    this.direction = !this.direction;
  }

  filterNotes() {
    this.filteredNotes = _.each(this.notes, (note: any) => {
      note.supportsName = this.supportPrompt(note);
    });

    if (this.supportFilter) {
      this.filteredNotes = _.filter(this.filteredNotes, (note: any) => {
        return this.ImNote.supportName(note) === this.supportFilter;
      });
    }

    if (this.categoryFilter) {
      this.filteredNotes = _.filter(this.filteredNotes, ({ category, categoryOtherVal }) => {
        if (category === 'Other') {
          return this.categoryFilter === 'Other' || categoryOtherVal === this.categoryFilter;
        }
        return category === this.categoryFilter;
      });
    }
  }

  supportPrompt(note) {
    if (this.ImNote.supportName(note)) {
      return 'Support: ' + this.ImNote.supportName(note);
    }
  }

  openMenu = ($mdOpenMenu, ev) => {
    $mdOpenMenu(ev);
  };

  openNoteModal = note => {
    const opts = {
      schoolId: note.schoolId,
      studentId: note.attachments.student.studentId,
      noteId: note._id,
      currentUser: this.currentUser,
    };

    this.NoteModal.show(opts).then(response => {
      if (response && response.note) {
        _.assign(note, response.note);
        this.notes = this._filterForActiveNotes(this.notes);
        this.filterNotes();
        this._setCategoryOptionsToShow(this.notes);
      }
    });
  };

  deleteNote = note => {
    this.NoteConfirmDeletionModal.show().then(() => {
      const noteId = note._id;
      note.status = 'DELETED';
      const filedsToPatch = ['status'];

      note
        .$patchFields(filedsToPatch)
        .then(() => {
          this._removeNoteById(noteId);
          this._setCategoryOptionsToShow(this.notes);
        })
        .catch(e => {
          this.ErrorModal.showAndRequireRefresh('Error:', e);
        });
    });
  };

  _removeNoteById = noteId => {
    _.remove(this.notes, { _id: noteId });
  };

  _getPanelPropsForNote = note => {
    const canView = note.canView(this.currentUser);
    const canEdit = canView && note.canEdit(this.currentUser);
    const canDelete = canView && note.canDelete(this.currentUser);
    const humanDateCreated = this.UtilitiesService.convertTimestampToLocalTime(note.dateCreated);
    const categoryOtherVal = note.category === 'Other' ? note.categoryOtherVal : null;

    return {
      canView,
      canEdit,
      canDelete,
      humanDateCreated,
      categoryOtherVal,
    };
  };

  _setNotesAfterInitialFetch = notes => {
    const {
      notesThatCanBeEditedHash,
      notesThatCanBeDeletedHash,
      notesThatCanBeViewed,
      categoryOtherVals,
      notesLastEditDate,
    } = _.reduce(
      notes,
      (acc, note: any) => {
        if (this._isNoteActive(note)) {
          const noteId = note._id;
          const { canView, canEdit, canDelete, humanDateCreated, categoryOtherVal } = this._getPanelPropsForNote(note);

          if (canView) acc.notesThatCanBeViewed.push(note);
          if (canEdit) acc.notesThatCanBeEditedHash[note._id] = true;
          if (canDelete) acc.notesThatCanBeDeletedHash[note._id] = true;
          if (categoryOtherVal) acc.categoryOtherVals.push(note.categoryOtherVal);

          // tslint:disable-next-line:max-line-length
          acc.notesLastEditDate[noteId] =
            this.UtilitiesService.convertTimestampToLocalTime(note.lastEditedAt) || humanDateCreated;
        }

        return acc;
      },
      {
        notesThatCanBeEditedHash: {},
        notesThatCanBeDeletedHash: {},
        notesThatCanBeViewed: [],
        categoryOtherVals: [],
        notesLastEditDate: {},
      },
    );

    // set notes
    this.filteredNotes = this.notes = notesThatCanBeViewed;
    // add efective email to object
    this.filteredNotes = this.filteredNotes.map(note => {
      note.createdByEffectiveEmail = this.imUser.getEffectiveEmail(note.createdBy);
      return note;
    });

    // set hashes
    _.assign(this.notesThatCanBeEditedHash, notesThatCanBeEditedHash);
    _.assign(this.notesThatCanBeDeletedHash, notesThatCanBeDeletedHash);
    _.assign(this.notesLastEditDate, notesLastEditDate);
    // set category options filter menu
    if (categoryOtherVals.length) {
      const newCategoryOpts = _.uniq(this.categoryOptionsToShow.concat(categoryOtherVals));
      const sortedNewCategoryOpts = this._sortStringArrCaseInsensitive(newCategoryOpts);

      this.categoryOptionsToShow = sortedNewCategoryOpts;
    }
  };

  _filterForActiveNotes = notes => {
    return _.filter(notes, this._isNoteActive);
  };

  _isNoteActive = note => {
    return note.status === 'ACTIVE';
  };

  _sortStringArrCaseInsensitive = arr => {
    const _arr = this.UtilitiesService.copyPOJO(arr);

    _arr.sort((a, b) => {
      return a.toLowerCase() < b.toLowerCase() ? -1 : 1;
    });

    return _arr;
  };

  _setCategoryOptionsToShow = notes => {
    const categoryOtherVals = _.reduce(
      notes,
      (acc, { categoryOtherVal }) => {
        if (categoryOtherVal) acc.push(categoryOtherVal);

        return acc;
      },
      [],
    );
    let categoryOptionsToShow = this._standardCategoryOptions.slice(0);

    if (categoryOtherVals.length) {
      const newCategoryOpts = _.uniq(categoryOptionsToShow.concat(categoryOtherVals));
      const sortedNewCategoryOpts = this._sortStringArrCaseInsensitive(newCategoryOpts);

      categoryOptionsToShow = sortedNewCategoryOpts;
    }

    this.categoryOptionsToShow = categoryOptionsToShow;
  };
}

angular.module('nv.singleStudent').controller('StudentNotesPanelCtrl', StudentNotesPanelCtrl);
